import type { LearningType, LearningKindCollectionType } from "common/learning";
import type { ContentSummary } from "common/contentSummary";

export type ComponentEvents = {
  "tds-app-launcher": {
    opened: undefined;
    link_clicked: {
      label: string;
    };
  };
  "tds-content-collection-item": ComponentEvents["tds-content-summary"];
  "tds-content-summary": ComponentEvents["tds-summary"];
  "tds-header": {
    help_clicked: undefined;
    logo_clicked: undefined;
    signin_clicked: {
      label: string;
    };
    signup_clicked: {
      label: string;
    };
    community_inbox_clicked: {
      label: string;
    };
  } & {
    [Event in keyof ComponentEvents["tds-app-launcher"] as `app_launcher_${keyof ComponentEvents["tds-app-launcher"]}`]: ComponentEvents["tds-app-launcher"][Event];
  } & {
    [Event in keyof ComponentEvents["tds-header-nav"] as `nav_${keyof ComponentEvents["tds-header-nav"]}`]: ComponentEvents["tds-header-nav"][Event];
  } & {
    [Event in keyof ComponentEvents["tds-header-profile"] as `profile_${keyof ComponentEvents["tds-header-profile"]}`]: ComponentEvents["tds-header-profile"][Event];
  };
  "tds-header-desktop": ComponentEvents["tds-header"];
  "tds-header-mobile": ComponentEvents["tds-header"];
  "tds-header-nav": {
    item_clicked: {
      label: string;
    };
  };
  "tds-header-nav-item": {
    item_clicked: {
      label: string;
    };
  };
  "tds-header-profile": {
    menu_opened: undefined;
  } & ComponentEvents["tds-user-menu"];
  "tds-footer": {
    language_selector_changed: {
      label: string;
    };
    link_clicked: {
      label: string;
    };
  };
  "tds-footer-global": {
    link_clicked: {
      label: string;
    };
  };
  "tds-footer-links": {
    link_clicked: {
      label: string;
    };
  };
  "tds-summary": {
    [Event in keyof ComponentEvents["tds-truncate"] as `truncate_${keyof ComponentEvents["tds-truncate"]}`]: ComponentEvents["tds-truncate"][Event];
  };
  "tds-truncate": {
    link_clicked: {
      label: string;
      href: string | null;
    };
  };
  "tds-user-menu": {
    link_clicked: {
      label: string;
      href: string | null;
    };
  };
  "thexternal-collection": {
    error: undefined;
    empty: undefined;
    loading: undefined;
    loaded: {
      content: ContentSummary[];
    };
    navigate: {
      content: ContentSummary;
    };
    cta_clicked: {
      content: {
        ctaHref: string | null;
        ctaLabel: string | null;
      };
    };
  };
  "tds-stamps-collection": {
    stamp_clicked: {
      apiName: string;
      isLocked: boolean;
    };
    filter_clicked: {
      filterOption: string;
    };
    show_more_clicked: undefined;
  };
};

// https://github.com/microsoft/TypeScript/issues/36444#issuecomment-578572999
export type TrackEventDetail<Tag extends keyof ComponentEvents> = {
  [K in keyof ComponentEvents[Tag]]: {
    eventName: K;
    payload: ComponentEvents[Tag][K];
  };
}[keyof ComponentEvents[Tag]];

export type UserActivityRecommendationPayload = {
  apiName: string;
  recommendationProvider?: string;
  recommendationId?: string;
  contentUid: string;
  type: string;
  recommenderName?: string;
  recommendationSetId?: string;
};

export type UserActivityRecommendationsPayload = {
  contentUids: string[];
  recommendationProvider: string;
  recommendationId: string;
};

export type TrackEvent<Tag extends keyof ComponentEvents> = CustomEvent<
  TrackEventDetail<Tag>
>;

export type UserActivityPayload = {
  pageLocation: string;
  pathName?: string;
};

export type UserActivityLabelPayload = UserActivityPayload & {
  label: string;
  href?: string;
};

export type UserActivityLearningPayload = {
  learning_type: LearningKindCollectionType;
};

export type UserActivityModulePayload = UserActivityPayload &
  UserActivityLearningPayload & {
    module_api_name: string;
    module_uid: string;
  };

export type UserActivityProjectPayload = UserActivityPayload &
  UserActivityLearningPayload & {
    project_api_name: string;
    project_uid: string;
  };

export type UserActivitySuperbadgePayload = UserActivityPayload &
  UserActivityLearningPayload & {
    superbadge_api_name: string;
    superbadge_uid: string;
  };

export type UserActivityTrailPayload = UserActivityPayload &
  UserActivityLearningPayload & {
    trail_api_name: string;
    trail_uid: string;
  };

export type UserActivityTrailmixPayload = UserActivityPayload &
  UserActivityLearningPayload & {
    trailmix_api_name: string;
    trailmix_uid: string;
  };

export type UserActivityLearningActionPayload =
  | UserActivityModulePayload
  | UserActivityProjectPayload
  | UserActivitySuperbadgePayload
  | UserActivityTrailPayload
  | UserActivityTrailmixPayload;

export type UserActivityQuizActionPayload = UserActivityPayload & {
  unit_content_uid: string;
  module_content_uid: string;
};

export type UserActivityCareersBasePayload = {
  tbid_role: string;
};

export type UserActivityCareersPageResultsFilteredPayload =
  UserActivityCareersBasePayload & {
    th_role?: string;
    th_role_filter?: string;
    location_filter?: string;
    radius_filter?: number;
  };

export type UserActivityCareersJobClickedPayload = UserActivityPayload &
  UserActivityCareersBasePayload & {
    job_id: string;
  };

export type UserActivityCareersMessageButtonClickedPayload =
  UserActivityPayload & {
    resumeId: string;
  };

export type UserActivityCareersAccountTypePayload = UserActivityPayload & {
  accountType: "candidate" | "employer";
};

export type UserActivityContentResultsPayload = UserActivityPayload & {
  filter_direction?: "horizontal" | "vertical";
};

export type UserActivityContentResultsTabChanged = UserActivityPayload & {
  tab_name: "all" | "learning" | "community" | "documentation";
};

export type UserActivityContentResultsFilteredPayload =
  UserActivityContentResultsPayload & {
    filter_api_name: string;
    filter_value?: string;
  };

export type UserActivityContentResultsSearchedPayload =
  UserActivityContentResultsPayload & {
    search_term: string;
  };

export type UserActivityContentResultsSortedPayload =
  UserActivityContentResultsPayload & {
    sort_by: string;
  };

export type UserActivityWizardContextPayload = UserActivityPayload & {
  context: Record<string, unknown>;
};

export type UserActivityWizardContextStepPayload =
  UserActivityWizardContextPayload & {
    step: string;
  };

export type UserActivityStepActionPayload = UserActivityPayload & {
  step: string;
  action: string;
};

export type UserActivityTrailRankPayload = UserActivityPayload & {
  rank: number;
  activeTrail: string;
};

export type UserActivityTrailsContextPayload =
  UserActivityWizardContextPayload & {
    recommendedTrails: string[];
  };

export type UserActivityTPOPayload = UserActivityPayload & {
  module_api_name: string;
  unit_api_name: string;
};

export type UserActivityTPOLaunchPayload = UserActivityTPOPayload & {
  playground_info: {
    playground_type: string;
    template?: {
      name: string;
      provider: string;
    };
  };
};

export type UserActivityAnnouncementPayload = UserActivityPayload & {
  announcement_id: string;
};

export type UserActivityCelebrationPayload = UserActivityPayload & {
  milestone: string;
  type: string;
};

export type UserActivityEvents = {
  announcement_seen: UserActivityAnnouncementPayload;
  announcement_dismissed: UserActivityAnnouncementPayload;
  modal_opened: UserActivityPayload;
  modal_dismissed: UserActivityPayload;
  app_launcher_clicked: UserActivityPayload;
  app_launcher_link_clicked: UserActivityLabelPayload;
  badge_rating_clicked: UserActivityPayload & {
    module_api_name: string;
    points: number;
  };
  careers_hire_me_clicked: UserActivityPayload;
  careers_page_visited: UserActivityCareersBasePayload;
  careers_page_results_filtered: UserActivityCareersPageResultsFilteredPayload;
  careers_job_clicked: UserActivityCareersJobClickedPayload;
  careers_show_more_clicked: UserActivityCareersBasePayload;
  careers_message_button_clicked: UserActivityCareersMessageButtonClickedPayload;
  careers_onboarding_completed: UserActivityCareersAccountTypePayload;
  careers_searched: UserActivityCareersAccountTypePayload;
  celebration_cta_clicked: UserActivityCelebrationPayload;
  celebration_page_viewed: UserActivityCelebrationPayload;
  community_inbox_clicked: UserActivityLabelPayload;
  content_results_filtered: UserActivityContentResultsFilteredPayload;
  content_results_reset: UserActivityContentResultsPayload;
  content_results_searched: UserActivityContentResultsSearchedPayload;
  content_results_sorted: UserActivityContentResultsSortedPayload;
  content_results_tab_changed: UserActivityContentResultsTabChanged;
  check_challenge_clicked: UserActivityPayload;
  cta_clicked:
    | (UserActivityLabelPayload & {
        ctaType: string;
      })
    | {
        ctaHref: string;
      };
  details_clicked: UserActivityLearningActionPayload;
  favorite_clicked: UserActivityLearningActionPayload;
  header_profile_clicked: UserActivityPayload;
  help_clicked: UserActivityPayload;
  image_clicked: UserActivityPayload & {
    ctaHref: string;
  };
  language_selector_changed: UserActivityLabelPayload;
  link_clicked: UserActivityLabelPayload;
  logo_clicked: UserActivityPayload;
  module_clicked: UserActivityModulePayload;
  my_profile_clicked: UserActivityLabelPayload;
  nav_clicked: UserActivityLabelPayload;
  preferences_updated: UserActivityWizardContextPayload;
  profile_menu_opened: UserActivityPayload;
  project_clicked: UserActivityProjectPayload;
  quiz_retake_clicked: UserActivityQuizActionPayload;
  recommendation_clicked: UserActivityRecommendationPayload;
  recommendation_items_viewed: UserActivityRecommendationsPayload;
  superbadge_clicked: UserActivitySuperbadgePayload;
  streak_celebration_modal_viewed: UserActivityPayload;
  streak_encouragement_modal_viewed: UserActivityPayload;
  streak_explainer_modal_viewed: UserActivityPayload;
  agentblazer_renewal_modal_viewed: UserActivityPayload;
  tpo_create_clicked: UserActivityTPOPayload;
  tpo_launch_clicked: UserActivityTPOLaunchPayload;
  trail_clicked: UserActivityTrailPayload;
  trailmix_clicked: UserActivityTrailmixPayload;
  unfavorite_clicked: UserActivityLearningActionPayload;
  add_to_trailmix_clicked: UserActivityLearningActionPayload;
  wizard_opened: UserActivityPayload;
  wizard_dismissed: UserActivityWizardContextStepPayload;
  wizard_state_changed: UserActivityWizardContextStepPayload;
  wizard_completed: UserActivityWizardContextPayload;
  wizard_button_clicked: UserActivityStepActionPayload;
  wizard_active_trail_selected: UserActivityTrailRankPayload;
  wizard_trails_recommended: UserActivityTrailsContextPayload;
};

export type UserActivityEventDetail<Event extends keyof UserActivityEvents> = {
  eventName: keyof UserActivityEvents[Event];
  payload: UserActivityEvents[Event][keyof UserActivityEvents[Event]];
};

export type UserActivityEvent<Event extends keyof UserActivityEvents> =
  CustomEvent<UserActivityEventDetail<Event>>;

export function createTrack<Tag extends keyof ComponentEvents>(
  element: EventTarget
) {
  return function <Event extends keyof ComponentEvents[Tag]>(
    eventName: Event,
    payload: ComponentEvents[Tag][Event]
  ) {
    track(element, eventName, payload);
  };
}

export function track<Tag extends keyof ComponentEvents>(
  element: EventTarget,
  eventName: keyof ComponentEvents[Tag],
  payload: ComponentEvents[Tag][typeof eventName]
) {
  const trackEvent = new CustomEvent("track", {
    detail: {
      eventName,
      payload: {
        ...(payload ? payload : {}),
      },
    },
  });
  element.dispatchEvent(trackEvent);
}

export function trackUserActivity<Event extends keyof UserActivityEvents>(
  element: EventTarget,
  eventName: Event,
  payload: UserActivityEvents[Event]
) {
  const trackEvent = new CustomEvent("trailhead_track", {
    bubbles: true,
    composed: true,
    detail: {
      eventName,
      payload: {
        ...(payload ? payload : {}),
      },
    },
  });
  element.dispatchEvent(trackEvent);
}

export function trackUserActivityLearning(
  element: EventTarget,
  learning: LearningType,
  payload: UserActivityPayload
) {
  const activityPayload = getUserActivityLearningActionPayload(
    learning,
    payload
  );
  switch (activityPayload.learning_type) {
    // TODO: Badge will be added in the future, for now treat it as a module
    case "BADGE":
    case "MODULE":
      trackUserActivity(
        element,
        "module_clicked",
        activityPayload as UserActivityModulePayload
      );
      break;
    case "PROJECT":
      trackUserActivity(
        element,
        "project_clicked",
        activityPayload as UserActivityProjectPayload
      );
      break;
    case "SUPERBADGE":
      trackUserActivity(
        element,
        "superbadge_clicked",
        activityPayload as UserActivitySuperbadgePayload
      );
      break;
    case "TRAIL":
    case "LEARNINGPATH":
      trackUserActivity(
        element,
        "trail_clicked",
        activityPayload as UserActivityTrailPayload
      );
      break;
    case "TRAILMIX":
      trackUserActivity(
        element,
        "trailmix_clicked",
        activityPayload as UserActivityTrailmixPayload
      );
      break;
    default:
      break;
  }
}

export function getUserActivityLearningActionPayload(
  learning: LearningType,
  payload: UserActivityPayload
): UserActivityLearningActionPayload {
  let type = learning.type as LearningKindCollectionType;
  switch (type) {
    // TODO: Badge will be added in the future, for now treat it as a module
    case "BADGE":
    case "MODULE":
      return {
        ...payload,
        learning_type: type,
        module_api_name: learning.name,
        module_uid: learning.id,
      };
    case "PROJECT":
      return {
        ...payload,
        learning_type: type,
        project_api_name: learning.name,
        project_uid: learning.id,
      };
    case "SUPERBADGE":
      return {
        ...payload,
        learning_type: type,
        superbadge_api_name: learning.name,
        superbadge_uid: learning.id,
      };
    case "TRAIL":
      return {
        ...payload,
        learning_type: type,
        trail_api_name: learning.name,
        trail_uid: learning.id,
      };
    case "LEARNINGPATH":
      return {
        ...payload,
        learning_type: type,
        trail_api_name: learning.name,
        trail_uid: learning.id,
      };
    case "TRAILMIX":
      return {
        ...payload,
        learning_type: type,
        trailmix_api_name: learning.name,
        trailmix_uid: learning.id,
      };
    default:
      return {} as never;
  }
}
